import React, { useState, useEffect, useContext, useRef } from 'react';
import { AuthContext } from '../App';
import Layout from '../components/Layout';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, FileText, Download, Eye, Trash2, Filter, X, CheckCircle, AlertCircle, Clock, Search, Bot, Sparkles, PenTool } from 'lucide-react';
import { formatNumberES } from '@/lib/utils';

export const Facturas = () => {
  const { API, token } = useContext(AuthContext);
  const [facturas, setFacturas] = useState([]);
  const [facturasFiltradas, setFacturasFiltradas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [archivosSeleccionados, setArchivosSeleccionados] = useState([]);
  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  
  // Filtros
  const [filtroTipo, setFiltroTipo] = useState('todas');
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroClienteProveedor, setFiltroClienteProveedor] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');
  const [busquedaClienteProveedor, setBusquedaClienteProveedor] = useState('');
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    cargarFacturas();
  }, []);

  useEffect(() => {
    filtrarFacturas();
  }, [facturas, filtroTipo, filtroEstado, filtroTexto, filtroClienteProveedor, filtroFechaDesde, filtroFechaHasta]);

  const cargarFacturas = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/facturas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFacturas(response.data);
      setFacturasFiltradas(response.data);
    } catch (error) {
      toast.error('Error al cargar facturas');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const archivos = Array.from(e.dataTransfer.files);
      setArchivosSeleccionados(archivos);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const archivos = Array.from(e.target.files);
      setArchivosSeleccionados(archivos);
    }
  };

  const procesarFacturas = async () => {
    if (archivosSeleccionados.length === 0) {
      toast.error('Selecciona al menos un archivo');
      return;
    }

    try {
      setProcesando(true);
      const formData = new FormData();
      archivosSeleccionados.forEach(archivo => {
        formData.append('files', archivo);
      });

      const response = await axios.post(`${API}/facturas/procesar`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const { facturas, errores, mensaje } = response.data;
      
      if (mensaje) {
        // El backend ya proporciona un mensaje resumen
        if (errores && errores.length > 0) {
          toast.warning(mensaje);
        } else {
          toast.success(mensaje);
        }
      }
      
      if (errores && errores.length > 0) {
        errores.forEach(error => {
          // El backend devuelve "mensaje" con el texto del error, no "error"
          const mensajeError = error.mensaje || (typeof error.error === 'string' ? error.error : 'Error desconocido');
          toast.error(`${error.archivo}: ${mensajeError}`);
        });
      }

      setArchivosSeleccionados([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      await cargarFacturas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar facturas');
    } finally {
      setProcesando(false);
    }
  };

  const filtrarFacturas = () => {
    let filtradas = [...facturas];

    if (filtroTipo !== 'todas') {
      filtradas = filtradas.filter(f => f.tipo === filtroTipo);
    }

    if (filtroEstado !== 'todos') {
      filtradas = filtradas.filter(f => f.estado === filtroEstado);
    }

    if (filtroTexto.trim() !== '') {
      const textoBusqueda = filtroTexto.toLowerCase();
      filtradas = filtradas.filter(f => 
        (f.numero_factura && f.numero_factura.toLowerCase().includes(textoBusqueda))
      );
    }

    if (filtroClienteProveedor.trim() !== '') {
      const nombreBusqueda = filtroClienteProveedor.toLowerCase();
      filtradas = filtradas.filter(f => 
        (f.nombre_cliente && f.nombre_cliente.toLowerCase().includes(nombreBusqueda)) ||
        (f.nombre_proveedor && f.nombre_proveedor.toLowerCase().includes(nombreBusqueda))
      );
    }

    if (filtroFechaDesde) {
      const fechaDesde = new Date(filtroFechaDesde);
      fechaDesde.setHours(0, 0, 0, 0);
      filtradas = filtradas.filter(f => {
        if (!f.fecha_emision) return false;
        const fechaEmision = new Date(f.fecha_emision);
        fechaEmision.setHours(0, 0, 0, 0);
        return fechaEmision >= fechaDesde;
      });
    }

    if (filtroFechaHasta) {
      const fechaHasta = new Date(filtroFechaHasta);
      fechaHasta.setHours(23, 59, 59, 999);
      filtradas = filtradas.filter(f => {
        if (!f.fecha_emision) return false;
        const fechaEmision = new Date(f.fecha_emision);
        fechaEmision.setHours(0, 0, 0, 0);
        return fechaEmision <= fechaHasta;
      });
    }

    setFacturasFiltradas(filtradas);
  };

  const clientesProveedoresSugerencias = React.useMemo(() => {
    const nombres = new Set();
    facturas.forEach(f => {
      if (f.nombre_cliente) nombres.add(f.nombre_cliente);
      if (f.nombre_proveedor) nombres.add(f.nombre_proveedor);
    });
    return Array.from(nombres).filter(nombre => 
      nombre.toLowerCase().includes(busquedaClienteProveedor.toLowerCase())
    ).slice(0, 10);
  }, [facturas, busquedaClienteProveedor]);

  const seleccionarClienteProveedor = (nombre) => {
    setFiltroClienteProveedor(nombre);
    setBusquedaClienteProveedor(nombre);
    setMostrarSugerencias(false);
  };

  const limpiarFiltros = () => {
    setFiltroTipo('todas');
    setFiltroEstado('todos');
    setFiltroTexto('');
    setFiltroClienteProveedor('');
    setBusquedaClienteProveedor('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
  };

  const descargarArchivo = async (facturaId, archivoPath) => {
    try {
      const response = await axios.get(`${API}/facturas/${facturaId}/archivo`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const contentDisposition = response.headers['content-disposition'];
      let nombreArchivo = `factura_${facturaId}`;
      
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches && matches[1]) {
          nombreArchivo = matches[1].replace(/['"]/g, '');
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombreArchivo);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Archivo descargado');
    } catch (error) {
      toast.error('Error al descargar archivo');
    }
  };

  const verDetalle = (factura) => {
    setFacturaSeleccionada(factura);
    setModalAbierto(true);
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      pendiente: { label: 'Pendiente', variant: 'secondary', icon: Clock },
      pagada: { label: 'Pagada', variant: 'default', icon: CheckCircle },
      vencida: { label: 'Vencida', variant: 'destructive', icon: AlertCircle }
    };
    const estadoInfo = estados[estado] || estados.pendiente;
    const Icon = estadoInfo.icon;
    return (
      <Badge variant={estadoInfo.variant} className="flex items-center gap-1">
        <Icon size={12} />
        {estadoInfo.label}
      </Badge>
    );
  };

  const getTipoBadge = (tipo) => {
    return tipo === 'emitida' ? (
      <Badge variant="default" className="bg-green-600">Emitida</Badge>
    ) : (
      <Badge variant="default" className="bg-blue-600">Recibida</Badge>
    );
  };

  const tieneFiltrosActivos = filtroTipo !== 'todas' || filtroEstado !== 'todos' || filtroTexto.trim() !== '' || filtroClienteProveedor.trim() !== '' || filtroFechaDesde !== '' || filtroFechaHasta !== '';

  return (
    <Layout>
      <div className="container mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Facturas</h1>
            <p className="text-gray-600 mt-1">Gestiona tus facturas emitidas y recibidas</p>
          </div>
        </div>

        {/* Animación de procesamiento */}
        {procesando && (
          <Card className="p-6 mb-6 border-2 border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <div className="flex flex-col items-center justify-center py-8">
              <div className="relative mb-6">
                {/* Bot de IA con animación */}
                <div className="relative">
                  <Bot 
                    size={64} 
                    className="text-blue-600 animate-pulse"
                    style={{
                      animation: 'float 2s ease-in-out infinite'
                    }}
                  />
                  {/* Efectos de brillo alrededor del bot */}
                  <Sparkles 
                    size={24} 
                    className="absolute -top-2 -right-2 text-yellow-400 animate-spin"
                    style={{
                      animationDuration: '2s'
                    }}
                  />
                  <Sparkles 
                    size={20} 
                    className="absolute -bottom-1 -left-1 text-purple-400 animate-spin"
                    style={{
                      animationDuration: '3s',
                      animationDirection: 'reverse'
                    }}
                  />
                </div>
                
                {/* Documentos flotando alrededor */}
                <div className="absolute top-0 left-0 w-full h-full">
                  <FileText 
                    size={32} 
                    className="absolute top-4 -left-8 text-blue-400 opacity-60"
                    style={{
                      animation: 'floatDocument 3s ease-in-out infinite',
                      animationDelay: '0s'
                    }}
                  />
                  <FileText 
                    size={28} 
                    className="absolute bottom-4 -right-6 text-indigo-400 opacity-60"
                    style={{
                      animation: 'floatDocument 3s ease-in-out infinite',
                      animationDelay: '1.5s'
                    }}
                  />
                </div>
              </div>
              
              {/* Texto animado */}
              <div className="text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-2 flex items-center justify-center gap-2">
                  <PenTool size={20} className="animate-pulse" />
                  Procesando facturas con IA...
                </h3>
                <p className="text-gray-600 text-sm">
                  La inteligencia artificial está analizando y extrayendo datos de tus facturas
                </p>
                <div className="flex items-center justify-center gap-1 mt-4">
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
            
            <style>{`
              @keyframes float {
                0%, 100% {
                  transform: translateY(0px) rotate(0deg);
                }
                50% {
                  transform: translateY(-10px) rotate(5deg);
                }
              }
              
              @keyframes floatDocument {
                0%, 100% {
                  transform: translateY(0px) translateX(0px) rotate(0deg);
                  opacity: 0.6;
                }
                50% {
                  transform: translateY(-15px) translateX(10px) rotate(10deg);
                  opacity: 0.9;
                }
              }
            `}</style>
          </Card>
        )}

        {/* Zona de subida de archivos */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
            <Upload size={20} />
            Subir Facturas
          </h2>
          
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Upload size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-600 mb-2">
              Arrastra archivos aquí o haz clic para seleccionar
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Formatos soportados: PDF, JPG, JPEG, PNG
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
            >
              Seleccionar Archivos
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>

          {archivosSeleccionados.length > 0 && (
            <div className="mt-4">
              <p className="text-sm font-medium mb-2">
                Archivos seleccionados ({archivosSeleccionados.length}):
              </p>
              <div className="space-y-2">
                {archivosSeleccionados.map((archivo, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm">{archivo.name}</span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const nuevos = [...archivosSeleccionados];
                        nuevos.splice(index, 1);
                        setArchivosSeleccionados(nuevos);
                      }}
                    >
                      <X size={16} />
                    </Button>
                  </div>
                ))}
              </div>
              <Button
                onClick={procesarFacturas}
                disabled={procesando}
                className="mt-4"
              >
                {procesando ? (
                  <span className="flex items-center gap-2">
                    <Bot size={16} className="animate-pulse" />
                    Procesando con IA...
                  </span>
                ) : (
                  `Procesar ${archivosSeleccionados.length} archivo(s)`
                )}
              </Button>
            </div>
          )}
        </Card>

        {/* Filtros y lista de facturas */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              <FileText size={20} />
              Lista de Facturas
            </h2>
            <div className="flex items-center gap-2">
              <Badge variant="secondary">{facturasFiltradas.length} factura(s)</Badge>
            </div>
          </div>

          {/* Filtros */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Tipo</Label>
              <Select value={filtroTipo} onValueChange={setFiltroTipo}>
                <SelectTrigger>
                  <SelectValue placeholder="Todas" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas</SelectItem>
                  <SelectItem value="emitida">Emitidas</SelectItem>
                  <SelectItem value="recibida">Recibidas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Estado</Label>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem value="pagada">Pagada</SelectItem>
                  <SelectItem value="vencida">Vencida</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Número de Factura</Label>
              <div className="relative">
                <Search size={16} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Buscar por número..."
                  value={filtroTexto}
                  onChange={(e) => setFiltroTexto(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="relative">
              <Label className="text-xs text-gray-500 mb-1 block">Cliente/Proveedor</Label>
              <div className="relative">
                <Search size={16} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 z-10" />
                <Input
                  ref={searchInputRef}
                  placeholder="Buscar cliente/proveedor..."
                  value={busquedaClienteProveedor}
                  onChange={(e) => {
                    setBusquedaClienteProveedor(e.target.value);
                    setFiltroClienteProveedor(e.target.value);
                    setMostrarSugerencias(true);
                  }}
                  onFocus={() => setMostrarSugerencias(true)}
                  onBlur={() => {
                    setTimeout(() => setMostrarSugerencias(false), 200);
                  }}
                  className="pl-8"
                />
                {busquedaClienteProveedor && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-0 top-0 h-full px-2"
                    onClick={() => {
                      setBusquedaClienteProveedor('');
                      setFiltroClienteProveedor('');
                    }}
                  >
                    <X size={14} />
                  </Button>
                )}
                {mostrarSugerencias && clientesProveedoresSugerencias.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-auto">
                    {clientesProveedoresSugerencias.map((nombre, index) => (
                      <div
                        key={index}
                        className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          seleccionarClienteProveedor(nombre);
                        }}
                      >
                        {nombre}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Fecha Desde</Label>
              <Input
                type="date"
                value={filtroFechaDesde}
                onChange={(e) => setFiltroFechaDesde(e.target.value)}
              />
            </div>

            <div>
              <Label className="text-xs text-gray-500 mb-1 block">Fecha Hasta</Label>
              <Input
                type="date"
                value={filtroFechaHasta}
                onChange={(e) => setFiltroFechaHasta(e.target.value)}
                min={filtroFechaDesde || undefined}
              />
            </div>
          </div>

          {tieneFiltrosActivos && (
            <div className="mb-4">
              <Button variant="outline" size="sm" onClick={limpiarFiltros}>
                <X size={14} className="mr-1" />
                Limpiar Filtros
              </Button>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">Cargando facturas...</div>
          ) : facturasFiltradas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No hay facturas {tieneFiltrosActivos ? 'que coincidan con los filtros' : ''}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Número</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Cliente/Proveedor</TableHead>
                    <TableHead>Tipología</TableHead>
                    <TableHead>Fecha Emisión</TableHead>
                    <TableHead>Total</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {facturasFiltradas.map((factura) => (
                    <TableRow key={factura.id}>
                      <TableCell className="font-medium">
                        {factura.numero_factura || '-'}
                      </TableCell>
                      <TableCell>{getTipoBadge(factura.tipo)}</TableCell>
                      <TableCell>
                        {factura.tipo === 'emitida' ? factura.nombre_cliente : factura.nombre_proveedor || '-'}
                      </TableCell>
                      <TableCell>{factura.nombre_tipologia || '-'}</TableCell>
                      <TableCell>
                        {factura.fecha_emision 
                          ? new Date(factura.fecha_emision).toLocaleDateString('es-ES')
                          : '-'}
                      </TableCell>
                      <TableCell>{formatNumberES(factura.total)}€</TableCell>
                      <TableCell>{getEstadoBadge(factura.estado)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => verDetalle(factura)}
                          >
                            <Eye size={16} />
                          </Button>
                          {factura.archivo_path && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => descargarArchivo(factura.id, factura.archivo_path)}
                            >
                              <Download size={16} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </Card>

        {/* Modal de Detalle */}
        <Dialog open={modalAbierto} onOpenChange={setModalAbierto}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Detalle de Factura</DialogTitle>
              <DialogDescription>
                Información completa de la factura
              </DialogDescription>
            </DialogHeader>
            {facturaSeleccionada && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs text-gray-500">Número de Factura</Label>
                    <p className="font-medium">{facturaSeleccionada.numero_factura || '-'}</p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Tipo</Label>
                    <div className="mt-1">{getTipoBadge(facturaSeleccionada.tipo)}</div>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">
                      {facturaSeleccionada.tipo === 'emitida' ? 'Cliente' : 'Proveedor'}
                    </Label>
                    <p className="font-medium">
                      {facturaSeleccionada.tipo === 'emitida' 
                        ? (facturaSeleccionada.nombre_cliente || '-')
                        : (facturaSeleccionada.nombre_proveedor || '-')}
                    </p>
                  </div>
                  <div>
                    <Label className="text-xs text-gray-500">Fecha de Emisión</Label>
                    <p>
                      {facturaSeleccionada.fecha_emision 
                        ? new Date(facturaSeleccionada.fecha_emision).toLocaleDateString('es-ES')
                        : '-'}
                    </p>
                  </div>
                  {facturaSeleccionada.fecha_vencimiento && (
                    <div>
                      <Label className="text-xs text-gray-500">Fecha de Vencimiento</Label>
                      <p>
                        {new Date(facturaSeleccionada.fecha_vencimiento).toLocaleDateString('es-ES')}
                      </p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs text-gray-500">Estado</Label>
                    <div className="mt-1">{getEstadoBadge(facturaSeleccionada.estado)}</div>
                  </div>
                </div>

                {facturaSeleccionada.nombre_tipologia && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-gray-500">Tipología</Label>
                    <p className="font-medium">{facturaSeleccionada.nombre_tipologia}</p>
                  </div>
                )}

                {facturaSeleccionada.concepto_general && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-gray-500">Concepto General</Label>
                    <p>{facturaSeleccionada.concepto_general}</p>
                  </div>
                )}

                {facturaSeleccionada.lineas_factura && facturaSeleccionada.lineas_factura.length > 0 && (
                  <div className="border-t pt-4">
                    <Label className="text-xs text-gray-500 mb-2 block">Líneas de Factura</Label>
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Descripción</TableHead>
                            <TableHead>Cantidad</TableHead>
                            <TableHead>P. Unitario</TableHead>
                            <TableHead>IVA</TableHead>
                            <TableHead>Importe</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {facturaSeleccionada.lineas_factura.map((linea, index) => (
                            <TableRow key={index}>
                              <TableCell>{linea.descripcion || '-'}</TableCell>
                              <TableCell>{linea.cantidad || '-'}</TableCell>
                              <TableCell>{linea.precio_unitario ? formatNumberES(linea.precio_unitario) + '€' : '-'}</TableCell>
                              <TableCell>{linea.iva ? linea.iva + '%' : '-'}</TableCell>
                              <TableCell>{linea.importe ? formatNumberES(linea.importe) + '€' : '-'}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </div>
                )}

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-2">Desglose</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span>Base Imponible:</span>
                      <span>{formatNumberES(facturaSeleccionada.base_imponible)}€</span>
                    </div>
                    <div className="flex justify-between">
                      <span>IVA ({facturaSeleccionada.porcentaje_iva}%):</span>
                      <span>{formatNumberES(facturaSeleccionada.importe_iva)}€</span>
                    </div>
                    {facturaSeleccionada.retencion > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>Retención IRPF ({facturaSeleccionada.retencion}%):</span>
                        <span>-{formatNumberES((facturaSeleccionada.base_imponible * facturaSeleccionada.retencion) / 100)}€</span>
                      </div>
                    )}
                    <div className="flex justify-between font-bold text-lg border-t pt-2">
                      <span>Total:</span>
                      <span>{formatNumberES(facturaSeleccionada.total)}€</span>
                    </div>
                  </div>
                </div>

                {facturaSeleccionada.archivo_path && (
                  <div className="border-t pt-4">
                    <Button
                      onClick={() => descargarArchivo(facturaSeleccionada.id, facturaSeleccionada.archivo_path)}
                      className="w-full"
                    >
                      <Download size={16} className="mr-2" />
                      Descargar Archivo
                    </Button>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Facturas;

